import React, { useContext, useEffect, useState } from "react";
import {
  Descriptions,
  Card,
  Typography,
  Tag,
  Button,
  Divider,
  List,
  Tooltip,
  Space,
} from "antd";
import {
  FileOutlined,
  MailOutlined,
  PhoneOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  CustomerFilesDrawer,
  FileUploadModal,
} from "../../components/customer";
import { useParams } from "react-router-dom";
import QuickbooksContext from "../../context/QuickbooksContext";
import DataContext from "../../context/DataContext";
import { USDollar } from "../../utils/func/currency";

const { Text } = Typography;

export const CustomerDetailPage = () => {
  const { idClient, idCustomer } = useParams();
  const { getCustomerData, customerInfo: customer } =
    useContext(QuickbooksContext);
  const { dataLoading } = useContext(DataContext);
  const [fileDrawerVisible, setFileDrawerVisible] = useState(false);
  const [isModalFileUploadVisible, setIsModalFileUploadVisible] =
    useState(false);

  useEffect(() => {
    idClient && idCustomer && getCustomerData(idCustomer, idClient, () => {});
  }, [idCustomer, idClient]);

  const addressDisplay = (addr: any) =>
    addr ? (
      <>
        {addr.Line1}, {addr.City}, {addr.CountrySubDivisionCode},{" "}
        {addr.PostalCode}
      </>
    ) : (
      <Text type="secondary">No Address</Text>
    );

  return (
    <div style={{ padding: "24px" }}>
      <Card
        className="summary-client__card"
        styles={{ body: { display: "none" } }}
        title={"Customer Details"}
        extra={[
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Space
              direction="horizontal"
              size={0}
              wrap
              style={{
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Tooltip title="View All Files" placement="bottom">
                <Button
                  type="link"
                  icon={<FileOutlined />}
                  size="large"
                  onClick={() => setFileDrawerVisible(true)}
                  style={{ minWidth: 100 }}
                >
                  View Files
                </Button>
              </Tooltip>
              <Tooltip title="New File" placement="bottom">
                <Button
                  type="link"
                  icon={<UploadOutlined />}
                  loading={dataLoading}
                  onClick={() => setIsModalFileUploadVisible(true)}
                  style={{ minWidth: 100 }}
                >
                  New File
                </Button>
              </Tooltip>
            </Space>
          </Space>,
        ]}
      ></Card>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Descriptions
          bordered
          size="small"
          column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2 }}
          style={{ width: "100%", marginBottom: 24, marginTop: 24 }}
        >
          <Descriptions.Item label="Display Name">
            {customer?.DisplayName}
          </Descriptions.Item>
          <Descriptions.Item label="Company">
            {customer?.CompanyName}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <Tooltip title={customer?.PrimaryEmailAddr?.Address}>
              <MailOutlined /> {customer?.PrimaryEmailAddr?.Address || "—"}
            </Tooltip>
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            <PhoneOutlined /> {customer?.PrimaryPhone?.FreeFormNumber || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Balance">
            {USDollar.format(customer.Balance || 0)}
          </Descriptions.Item>
          <Descriptions.Item label="Currency">
            {customer?.CurrencyRef?.name || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Active">
            <Tag color={customer?.Active ? "green" : "red"}>
              {customer?.Active ? "Yes" : "No"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Taxable">
            <Tag color={customer?.Taxable ? "blue" : "orange"}>
              {customer?.Taxable ? "Yes" : "No"}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        <Card title="Addresses" style={{ marginBottom: 24 }}>
          <Descriptions
            column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
            style={{ width: "100%" }}
          >
            <Descriptions.Item label="Billing Address">
              {addressDisplay(customer?.BillAddr)}
            </Descriptions.Item>
            <Descriptions.Item label="Shipping Address">
              {addressDisplay(customer?.ShipAddr)}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Notes" style={{ marginBottom: 24 }}>
          <List
            size="small"
            itemLayout="horizontal"
            data-testid="customer-notes"
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
            }}
            style={{ maxHeight: 300, overflowY: "auto" }}
            dataSource={customer?.CustomerNotes}
            renderItem={(note: any) => (
              <List.Item>
                <List.Item.Meta
                  title={new Date(note.date).toLocaleString()}
                  description={note.body}
                />
              </List.Item>
            )}
            locale={{ emptyText: "No notes available" }}
          />
          <Divider />
        </Card>
      </Space>

      <CustomerFilesDrawer
        idClient={idClient || ""}
        idCustomer={idCustomer || ""}
        open={fileDrawerVisible}
        onClose={() => setFileDrawerVisible(false)}
      />
      <FileUploadModal
        open={isModalFileUploadVisible}
        idClient={idClient || ""}
        idCustomer={idCustomer || undefined}
        onClose={() => setIsModalFileUploadVisible(false)}
        onUploadSuccess={() => setFileDrawerVisible(true)}
      />
    </div>
  );
};
